import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { NotificationsActive } from '@mui/icons-material';
import { useGenericQuery } from '../../../../../services/react-query/generic.query';
import { NotificationTypes, UserTypes } from '../../../../../types';

interface Props {
    open: boolean;
    address: string;
    alreadyNotified: NotificationTypes.NotifiedUser[];
    isLoading: boolean;
    onNotify(users: UserTypes.Model[]): void;
    onClose(): void;
}

const userName = (user: UserTypes.Model) => `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
const userInitials = (user: UserTypes.Model) =>
    `${user?.firstName?.charAt(0) ?? ''}${user?.lastName?.charAt(0) ?? ''}` || '?';

// Single-step replacement for the old NotifyUser + NotifyUsersModal stack:
// search, pick, and notify in one dialog. Users already on the report's
// notify list are shown for context and excluded from the options.
export const NotifyTeamDialog: React.FC<Props> = ({ open, address, alreadyNotified, isLoading, onNotify, onClose }) => {
    const {
        data: users,
        isLoading: isUsersLoading,
    } = useGenericQuery<UserTypes.Model[]>('fetchUsersToNotify', 'users/getSubscriptionUsers');

    const [selected, setSelected] = useState<UserTypes.Model[]>([]);

    useEffect(() => {
        if (!open) setSelected([]);
    }, [open]);

    const alreadyNotifiedIds = new Set(alreadyNotified.map((user) => user.id));
    const options = (users ?? []).filter((user) => !alreadyNotifiedIds.has(user.id));

    return (
        <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle fontWeight={500} textAlign={'left'}>
                Notify Team Members
            </DialogTitle>
            <DialogContent sx={{ mt: 1 }}>
                <DialogContentText textAlign={'left'} sx={{ mb: 2 }}>
                    Selected members are notified about the assessment for {address || 'this address'} and can open the saved report.
                </DialogContentText>

                <Autocomplete
                    multiple
                    options={options}
                    value={selected}
                    onChange={(_, value) => setSelected(value)}
                    getOptionLabel={userName}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    filterSelectedOptions
                    loading={isUsersLoading}
                    disabled={isLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={selected.length ? '' : 'Search team members'}
                        />
                    )}
                    renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', gap: 1.5 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#ECEFF2', color: '#64707C', fontSize: 12, fontWeight: 600 }}>
                                {userInitials(option)}
                            </Avatar>
                            <Typography variant="body2">{userName(option)}</Typography>
                        </Box>
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.id}
                                size="small"
                                avatar={<Avatar sx={{ bgcolor: '#ECEFF2', color: '#64707C' }}>{userInitials(option)}</Avatar>}
                                label={userName(option)}
                            />
                        ))
                    }
                />

                {alreadyNotified.length > 0 && (
                    <Box sx={{ mt: 2, bgcolor: '#F9FAFB', border: '1px solid #DFE3E8', borderRadius: 1, p: 1.5 }}>
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#919EAB' }}>
                            Already notified
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#212B36' }}>
                            {alreadyNotified.map((user) => user.name).join(', ')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <Box sx={{ borderTop: 1, m: 1, p: 1, gap: 2, display: 'flex', borderColor: 'divider', flexDirection: 'row-reverse' }}>
                <LoadingButton
                    variant="alternate"
                    loading={isLoading}
                    disabled={selected.length === 0}
                    onClick={() => onNotify(selected)}
                    startIcon={<NotificationsActive />}
                >
                    Notify{selected.length > 0 ? ` (${selected.length})` : ''}
                </LoadingButton>
                <Button variant="outlined-alternate" onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
            </Box>
        </Dialog>
    );
};
